import classNames from "classnames";
import { useReviewers } from "PFApp/hooks/activity_commons/participants";
import { Alert } from "PFComponents/alert";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { Typography } from "PFComponents/typography";
import { isDatePassed } from "PFCore/helpers/date";
import { getProfileName } from "PFCore/helpers/profile";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";

import { ActivityProfilesAvatarStack } from "../../../../components/activity_profiles_avatar_stack";
import ButtonLabel from "../../../parts/button_label";
import { useActivityShortlists } from "../../hooks/use_activity_shortlists";
import { useCurrentActivity } from "../../hooks/use_current_activity";
import { AddActivityProfilesModal } from "../activity_profiles/add_activity_profiles_modal";
import { AddProfilesTabs } from "../activity_profiles/constants";
import { useActivityChat } from "../hooks/use_activity_chat";
import { DeadlineModal } from "./deadline_modal";
import { ReviewDeadlineLabel } from "./review_deadline_label";
import css from "./review_section.module.scss";
import { ReviewerLabel } from "./reviewer_label";

export const OwnerFlow = () => {
  const { t } = useTranslation("activities", { keyPrefix: "show.shortlist.review" });
  const { t: trans } = useTranslation("translation");

  const activity = useCurrentActivity();
  const { allShortlists } = useActivityShortlists();
  const { reviewers, canWrite: canWriteReviewers } = useReviewers(activity);
  const { startChat } = useActivityChat();

  const [showDeadlineModal, setShowDeadlineModal] = useToggle(false);

  const reviewer = activity.review?.completed_by;
  const isDraft = activity.state === "draft";
  const deadlineDate = activity.review?.deadline;
  const deadlinePassed = isDatePassed(deadlineDate);
  const completedDate = activity.review?.completed_at;
  const canReview =
    !!allShortlists.length && ["pending", "shortlisting", "reviewing"].includes(activity.workflow_state);

  const [showRequestReview, setShowRequestReview] = useToggle(false);

  return (
    <>
      <ActivityProfilesAvatarStack
        className={css.profilesStack}
        label={t("review")}
        profiles={reviewers}
        actionLabel={t("addReviewer")}
        qaId="ActivityPageAddReviewerButton"
        handleActionClick={canWriteReviewers ? () => setShowRequestReview(true) : undefined}
        displayChatIcon={!isDraft}
        handleIconClick={startChat}
      />
      {reviewers.length === 0 && (
        <Typography withMargin variant="labelRegular" className={css.noReviewersLabel}>
          <Icon name="info" color="paletteBlue2" />
          {t("noReviewers")}
        </Typography>
      )}
      {completedDate && <ReviewerLabel name={getProfileName(reviewer)} date={completedDate} />}
      {!completedDate && deadlineDate && (
        <div className={classNames(css.label, css.inReviewLabel)}>
          {reviewers.length > 0 && (
            <ButtonLabel className={css.nowrap}>
              <Icon name="cross" size="sm" color="paletteRed0" />
              {t("inReview")}
            </ButtonLabel>
          )}
          <ReviewDeadlineLabel date={deadlineDate} onClick={setShowDeadlineModal} />
        </div>
      )}
      {!completedDate && !deadlineDate && reviewers.length > 0 && canReview && (
        <Button className={css.button} onClick={setShowDeadlineModal} text={t("setDeadline")} />
      )}
      {!completedDate && deadlinePassed && (
        <Alert
          className={css.label}
          kind="warning"
          action={{ onClick: setShowDeadlineModal, text: trans("edit") }}
          title={t("deadlinePassedOwner")}
          multiLineTitle
        />
      )}
      {showDeadlineModal && <DeadlineModal onClose={setShowDeadlineModal} />}
      {showRequestReview && (
        <AddActivityProfilesModal
          onClose={() => setShowRequestReview(false)}
          activity={activity}
          tabId={AddProfilesTabs.Reviewers}
        />
      )}
    </>
  );
};
